import CircularProgress from '@material-ui/core/CircularProgress';
import { observer } from 'mobx-react';
import { isNil } from 'ramda';
import React, { FunctionComponent } from 'react';
import { MutationStatus, QueryStatus } from 'react-query';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import { Box } from '~common/components/box.component';
import { Button, ButtonProps } from '~common/components/button/button.component';
import { isNilOrEmpty } from '~common/helper/global';
import { AnyTask } from '~common/helper/mobx-tasks';
import { CheckMarkIcon } from '~web/view/components/ui/icon/check-mark.icon';

const StateButtonInner: FunctionComponent<{
  isSuccessful: boolean;
  isLoading: boolean;
  successText: string;
}> = ({ isLoading, isSuccessful, successText, children }) => {
  if (isLoading) {
    return (
      <IconContainer>
        <CircularProgress size={20} thickness={6} color="inherit" />
      </IconContainer>
    );
  }
  if (isSuccessful) {
    if (!isNilOrEmpty(successText)) {
      return <IconContainer>{successText}</IconContainer>;
    }
    return (
      <IconContainer>
        <CheckMarkIcon />
      </IconContainer>
    );
  }
  return <ChildrenContainer>{children}</ChildrenContainer>;
};

export interface StateButtonProps extends ButtonProps {
  isSuccessful?: boolean;
  isLoading?: boolean;
  isInvalid?: boolean;
  task?: AnyTask; // if a task is set, it will be used to set the disabled, isLoading, and isSuccessful props of the button
  successText?: string;
}

export const StateButton: React.FunctionComponent<StateButtonProps> = observer(
  ({ children, disabled, isInvalid, isLoading, isSuccessful, task, successText, ...rest }) => {
    const loading = isLoading || task?.pending;
    const success = !loading && (isSuccessful || (task?.resolved && !isNil(task?.result)));
    const isDisabled = disabled || isInvalid || loading || success;

    // {..rest} has to be on the top because otherwise it overrides disabled
    return (
      <StyledButton
        {...rest}
        disabled={isDisabled}
        isSuccessful={success}
        successText={successText}
      >
        <StateButtonInner isLoading={loading} isSuccessful={success} successText={successText}>
          {children}
        </StateButtonInner>
      </StyledButton>
    );
  },
);

export interface QueryStateButtonProps extends StateButtonProps {
  queryStatus: QueryStatus | MutationStatus;
}

export const QueryStateButton: React.FunctionComponent<QueryStateButtonProps> = observer(
  ({ queryStatus, isLoading, isSuccessful, disabled, isInvalid, ...rest }) => {
    const loading = isLoading || queryStatus === 'loading';
    const success = !loading && (isSuccessful || queryStatus === 'success');
    const isDisabled = disabled || isInvalid || loading || success;

    return (
      <StateButton isLoading={loading} isSuccessful={success} disabled={isDisabled} {...rest} />
    );
  },
);

const StyledButton = styled(({ isSuccessful, successText, ...rest }) => <Button {...rest} />)<{
  isSuccessful: boolean;
  successText: string;
}>`
  &&,
  &&.disabled,
  &&:hover,
  &&:hover.disabled {
    ${(props): FlattenSimpleInterpolation | null => {
      if (props.isSuccessful) {
        if (props.successText) {
          return css`
            color: ${props.theme.colors.green};
          `;
        } else {
          return css`
            background-color: ${props.theme.colors.green};
          `;
        }
      }
      return null;
    }}
`;

const IconContainer = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  justify-content: center;
  align-items: center;
`;

const ChildrenContainer = styled(Box)`
  align-items: center;
  max-width: 100%;
`;



// WEBPACK FOOTER //
// /codebuild/output/src332225586/src/modules/common/src/components/button/state-button.component.tsx